import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { clearSessionToken } from "@/lib/api";

const AuthContext = createContext(null);

// Routes that don't need an auth probe. Prevents noisy 401s in the console
// when anonymous visitors land on marketing / legal pages.
const PUBLIC_PATHS = new Set(["/", "/contact", "/terms", "/refund-policy"]);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      // CRITICAL: If returning from OAuth callback, skip the /me check.
      // AuthCallback will exchange the session_id and establish the session first.
      if (window.location.hash?.includes("session_id=")) {
        setLoading(false);
        return;
      }
      // Skip auth probe on public routes — no cookie yet, no need to hit the API.
      if (PUBLIC_PATHS.has(window.location.pathname)) {
        setLoading(false);
        return;
      }
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearSessionToken();
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
